import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import Link from 'src/components/primitives/Link/Link';
import type { ComponentProps, ElementType } from 'react';
import type { ExtraProps } from 'react-markdown';
import { VaultOverviewSectionBox } from '../VaultOverviewSectionBox';
import { dlog } from '../../../../../dev';
import {
	usePlaceholdersReplacements,
	PlaceholdersReplacements,
} from '../../../../../hooks/usePlaceholdersReplacements';
import {
	FEES_PLACEHOLDER,
	MANAGEMENT_FEE_PLACEHOLDER,
	PERIOD_PLACEHOLDER,
} from 'src/constants/vaults/misc';

/**
 * Allowed custom tags in markdown:
 * <profit-share/> :: Displays the profit share fee
 * <management-fee/> :: Displays the management fee
 * <withdrawal-period/> :: Displays the withdrawal period
 */

const replaceCustomTags = (
	content: string,
	placeholdersReplacements: PlaceholdersReplacements
) => {
	// First split by lines to process each line separately
	const lines = content.split('\n');

	const processedLines = lines.map((line) => {
		// Check if line contains any custom tags
		const hasCustomTags =
			/<profit-share\/>|<management-fee\/>|<withdrawal-period\/>/.test(line);

		// Preserve list item markers
		const isListItem = /^[-*+]\s/.test(line);
		const listMarker = isListItem ? line.match(/^[-*+]\s/)?.[0] : '';
		const lineWithoutMarker = isListItem
			? line.slice(listMarker?.length ?? 0)
			: line;

		const processedLine = lineWithoutMarker
			.replace(
				/<profit-share\/>/g,
				`<span class="placeholder">${placeholdersReplacements[FEES_PLACEHOLDER]}</span>`
			)
			.replace(
				/<management-fee\/>/g,
				`<span class="placeholder">${placeholdersReplacements[MANAGEMENT_FEE_PLACEHOLDER]}</span>`
			)
			.replace(
				/<withdrawal-period\/>/g,
				`<span class="placeholder">${placeholdersReplacements[PERIOD_PLACEHOLDER]}</span>`
			);

		// Only wrap lines that contained custom tags, but preserve list markers
		return isListItem
			? `${listMarker}${
					hasCustomTags
						? `<span class="inline-block">${processedLine}</span>`
						: processedLine
				}`
			: hasCustomTags
				? `<span class="inline-block">${processedLine}</span>`
				: processedLine;
	});

	return processedLines.join('\n');
};

type Components = {
	[Key in Extract<ElementType, string>]?: ElementType<
		ComponentProps<Key> & ExtraProps
	>;
};

const ListItem = ({ children }: { children: React.ReactNode }) => (
	<li className="list-inside [&::marker]:pr-xs [&::marker]:text-text-default">
		{children}
	</li>
);

const components: Components = {
	h1: ({ children }) => (
		<span className="typo-t3 text-text-default">{children}</span>
	),
	h2: ({ children }) => (
		<span className="typo-t3 text-text-default">{children}</span>
	),
	h3: ({ children }) => (
		<span className="typo-t3 text-text-default">{children}</span>
	),
	h4: ({ children }) => (
		<span className="typo-t3 text-text-default">{children}</span>
	),
	h5: ({ children }) => (
		<span className="typo-t3 text-text-default">{children}</span>
	),
	h6: ({ children }) => (
		<span className="typo-t3 text-text-default">{children}</span>
	),
	ul: ({ children }) => <ul className="list-inside list-disc">{children}</ul>,
	ol: ({ children }) => (
		<ol className="list-inside list-decimal">{children}</ol>
	),
	li: ({ children }) => <ListItem>{children}</ListItem>,
	img: ({ src, alt }) => <img src={src} alt={alt} />,
	p: ({ children }) => <div className="gap-2">{children}</div>,
	strong: ({ children }) => (
		<span className="font-medium text-text-default">{children}</span>
	),
	em: ({ children }) => <span className="italic">{children}</span>,
	a: ({ children, href }) => (
		<Link href={href ?? ''} openInNewTab>
			{children}
		</Link>
	),
	div: ({ className, children }) => {
		return <div className={className}>{children}</div>;
	},
	span: ({ className, children }) => {
		if (className?.includes('placeholder')) {
			return (
				<span className="inline-block font-medium text-text-default">
					{children}
				</span>
			);
		}
		return <span className={className}>{children}</span>;
	},
	hr: () => <hr className="border-text-secondary" />,
};

const ContentBox = ({ content }: { content: string }) => {
	const placeholderReplacements = usePlaceholdersReplacements();

	// Replace custom-node tag with a div that has a specific class
	const processedContent = replaceCustomTags(content, placeholderReplacements);

	dlog(`markdown_debugging`, `processed_content`, processedContent);

	return (
		<VaultOverviewSectionBox>
			<Markdown components={components} rehypePlugins={[rehypeRaw]}>
				{processedContent}
			</Markdown>
		</VaultOverviewSectionBox>
	);
};

const VaultOverviewMarkdownContent = (props: { markdownContent: string }) => {
	if (!props.markdownContent) return null;

	const markdownToUse = props.markdownContent;

	// Split content only by H1 tags (lines starting with # )
	const sections = markdownToUse.split(/(?=^# )/m);

	return (
		<div className="flex flex-col gap-4">
			{sections.map((section, index) => (
				<ContentBox key={index} content={section} />
			))}
		</div>
	);
};

export default VaultOverviewMarkdownContent;
